import { ChevronLeftIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import { ReactNode } from "react";
import { FaPlus } from "react-icons/fa";
import { IoMdPerson } from "react-icons/io";
import { Link, useNavigate } from "react-router";

import { Organisation } from "@nens/brostar/openapi-client";

import BrostarIcon from "@/icons/brostar.svg?react";
import { useUser } from "@/providers/auth-provider";
import { useLastImportDate } from "@/queries/query-import-task";
import { useOrganisationsListAllPagesQuery } from "@/queries/query-organisations";

import { ImportBroIdBar } from "./components/import-bro-id-bar";
import { ImportButton } from "./components/import-button";

const NO_CHEVRON_LEFT_SPACING = 8;
const InnerMenuButton = ({ children }: { children: ReactNode }) => (
  <MenuButton p={2} px={3} onClick={(e) => e.stopPropagation()} w="full" textAlign="left">
    <ChevronLeftIcon /> {children}
  </MenuButton>
);

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000;

function getImportDateColor(date: Date): string {
  const age = Date.now() - date.getTime();
  if (age < ONE_WEEK_MS) return "green.500";
  if (age < THREE_MONTHS_MS) return "yellow.500";
  return "red.500";
}

function LastImportDate({ organisation }: { organisation?: Organisation }) {
  const { lastImportDate } = useLastImportDate();
  const formattedDate = lastImportDate ? moment(lastImportDate).format("YYYY-MM-DD") : null;

  return (
    <>
      {lastImportDate && formattedDate && (
        <Flex alignItems="center" mr={2}>
          <Tooltip label={`Laatste import: ${formattedDate}`}>
            <Text fontSize="xs" color={getImportDateColor(lastImportDate)} fontWeight="semibold">
              {formattedDate}
            </Text>
          </Tooltip>
        </Flex>
      )}
      <ImportBroIdBar organisation={organisation} />
      <ImportButton organisation={organisation} />
    </>
  );
}

export function Header() {
  const navigate = useNavigate();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const user = useUser();

  const { data } = useOrganisationsListAllPagesQuery();
  const organisation = (data?.results ?? []).find((org) => org.kvkNumber === user.kvk);
  const setLocation = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <Flex zIndex={1004} mb="3" pb={2}>
      <Heading display="flex" onClick={() => setLocation("/")} cursor="pointer">
        <BrostarIcon width={40} height={40} />
        <Text ml="4">BROSTAR</Text>
      </Heading>
      <Flex gap={2} flexGrow={1} justifyContent="end">
        <LastImportDate organisation={organisation} />
        <Link to="/profile/">
          <Button boxSize="button" p={0} variant="outlinePrimary">
            <IoMdPerson />
          </Button>
        </Link>
        <Menu onClose={onClose} isOpen={isOpen}>
          <MenuButton
            as={IconButton}
            boxSize="button"
            p={0}
            variant="primary"
            icon={<FaPlus />}
            title="Bericht toevoegen"
            onClick={onToggle}
          />
          <MenuList>
            <MenuItem as="div" p={0}>
              <Menu placement="left-start">
                <InnerMenuButton>GMN - Meetnet</InnerMenuButton>
                <MenuList>
                  <MenuItem onClick={() => setLocation("/gmn/startregistratie/")}>
                    Start registratie
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/gmn/meetpunt/")}>
                    Meetpunt toevoegen
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/gmn/beeindigen/")}>Beeindigen</MenuItem>
                </MenuList>
              </Menu>
            </MenuItem>
            <MenuItem as="div" p={0}>
              <Menu placement="left-start">
                <InnerMenuButton>GMW - Peilbuizen</InnerMenuButton>
                <MenuList>
                  <MenuItem onClick={() => setLocation("/gmw/constructie/")}>Constructie</MenuItem>
                  <MenuItem onClick={() => setLocation("/gmw/gebeurtenissen/")}>
                    Gebeurtenissen
                  </MenuItem>
                </MenuList>
              </Menu>
            </MenuItem>
            <MenuItem as="div" p={0}>
              <Menu placement="left-start">
                <InnerMenuButton>GLD - Grondwaterstanden</InnerMenuButton>
                <MenuList>
                  <MenuItem onClick={() => setLocation("/gld/startregistratie/")}>
                    Start registratie
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/gld/aanvulling/")}>Aanvulling</MenuItem>
                  <MenuItem onClick={() => setLocation("/gld/beeindigen/")}>Beeindigen</MenuItem>
                </MenuList>
              </Menu>
            </MenuItem>
            <MenuItem as="div" p={0}>
              <Menu placement="left-start">
                <InnerMenuButton>FRD - Formatieweerstand</InnerMenuButton>
                <MenuList>
                  <MenuItem onClick={() => setLocation("/frd/startregistratie/")}>
                    Start registratie
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/frd/meetconfiguratie/")}>
                    Meetconfiguraties
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/frd/meting/")}>Metingen</MenuItem>
                  <MenuItem onClick={() => setLocation("/frd/beeindigen/")}>Beëindigen</MenuItem>
                </MenuList>
              </Menu>
            </MenuItem>
            <MenuItem pl={NO_CHEVRON_LEFT_SPACING} onClick={() => setLocation("/gar/")}>
              GAR - Grondwaterkwaliteit
            </MenuItem>
            <MenuItem as="div" p={0}>
              <Menu placement="left-start">
                <InnerMenuButton>GUF - Grondwatergebruik</InnerMenuButton>
                <MenuList>
                  <MenuItem onClick={() => setLocation("/guf/startregistratie/")}>
                    Start registratie
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/guf/nieuwe-vergunning/")}>
                    Nieuwe vergunning
                  </MenuItem>
                  <MenuItem
                    onClick={() => setLocation("/guf/gerealiseerde-installatie-toevoegen/")}
                  >
                    Gerealiseerde installatie toevoegen
                  </MenuItem>
                  <MenuItem
                    onClick={() => setLocation("/guf/gerealiseerde-installatie-uitbreiden/")}
                  >
                    Gerealiseerde installatie uitbreiden
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/guf/hoogte/")}>Hoogte</MenuItem>
                  <MenuItem onClick={() => setLocation("/guf/putfunctie/")}>Putfunctie</MenuItem>
                  <MenuItem onClick={() => setLocation("/guf/gerealiseerd-deel-beeindigen/")}>
                    Gerealiseerd deel beëindigen
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/guf/beeindigen/")}>Beëindigen</MenuItem>
                </MenuList>
              </Menu>
            </MenuItem>
            <MenuItem as="div" p={0}>
              <Menu placement="left-start">
                <InnerMenuButton>GPD - Grondwaterproductie</InnerMenuButton>
                <MenuList>
                  <MenuItem onClick={() => setLocation("/gpd/startregistratie/")}>
                    Start registratie
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/gpd/rapport/")}>
                    Rapport toevoegen
                  </MenuItem>
                  <MenuItem onClick={() => setLocation("/gpd/beeindigen/")}>Beeindigen</MenuItem>
                </MenuList>
              </Menu>
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </Flex>
  );
}
